import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { SbSplitButton } from '../../../../services/global/global-interfaces';
import { API, ClientConnectionEstablishmentRequestDTO } from '../../../../services/api/api';
import { ToasterService, ToastType } from '../../../../services/toaster/toaster.service';
import { ErrorService } from '../../../../services/error.service';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StringTransformationService } from '../../../../services/transformation-helper/string-transformation.service';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { RequestClass } from '../../../../services/global/RequestClass';

type SplitButtonAction = 'delete';

@Component({
  selector: 'sb-table-client-connections-send',
  templateUrl: './send-client-connections-table.component.html',
  styleUrls: ['./send-client-connections-table.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SendClientConnectionsTableComponent extends RequestClass {
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly api = inject(API);
  private readonly toaster = inject(ToasterService);
  private readonly modalService = inject(ModalService);
  private readonly errorService = inject(ErrorService);
  private readonly translate = inject(TranslateService);

  // table wrapper variables
  data: ClientConnectionEstablishmentRequestDTO[] = [];

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  public refreshFromServer(): void {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .shared_ClientConnection_retrieveClientConnectionEstablishmentRequests(
        {
          connected: false,
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: requests => {
          this.data = this.buildSplitButtons(requests);
        },
        error: error => {
          this.errorService.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  public delete(connection: ClientConnectionEstablishmentRequestDTO): void {
    if (connection) {
      this.modalService
        .confirm({
          title: this.translate.instant('lang.clientManagement.connection.withdraw'),
          message: this.translate.instant('lang.clientManagement.connection.withdraw.infoText', { clientName: connection.requested.name }),
          yes: this.translate.instant('lang.clientManagement.connection.withdrawAction'),
          no: this.translate.instant('lang.globals.cancel'),
        })
        .then((result: boolean) => {
          if (result) {
            this.api
              .shared_ClientConnection_withdrawClientConnectionEstablishmentRequest(connection.requested.id)
              .pipe(take(1))
              .subscribe({
                next: () => {
                  this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.clientManagement.connection.withdrawn'));
                  this.refreshFromServer();
                },
                error: error => {
                  this.errorService.errorHandler(error);
                },
              });
          }
        });
    }
  }

  buildSplitButtons<T>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      item.sbButtons.push({
        icon: 'trash',
        iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
        action: 'delete',
        tooltip: 'lang.clientManagement.connection.withdraw',
      });
    });
    return result;
  }

  resolveSplitButtonAction(connection: ClientConnectionEstablishmentRequestDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'delete':
        this.delete(connection);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
